import parse from "html-react-parser"
import { useRef, useState } from "react"

import iconCollapse from "images/icons/icon-collapse.svg?raw"
import iconExpand from "images/icons/icon-expand.svg?raw"
import iconOpenNew from "images/icons/icon-open_new.svg?raw"
import iconX from "images/icons/icon-x.svg?raw"
import iconSearch from "images/icons/search.svg?raw"

import type { SelectorData, SelectorOption, UIEvent } from "../types"
import { CardShell } from "./card_shell"
import styles from "./selector_card.module.scss"

interface Props {
  ui: UIEvent
  onRespond: (uiId: string, actionId: string, value: unknown, displayText?: string) => void
  // The choice card's last row. It closes the card like the dismiss control, so the user can type
  // an answer of their own instead of tapping one of the agent's suggestions.
  somethingElse?: boolean
}

const COLLAPSED_COUNT = 5

const SelectorCard = ({ ui, onRespond, somethingElse = false }: Props) => {
  const [answered, setAnswered] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [query, setQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { prompt, options, icon_url: cardIcon, brand_color: cardBrand } = ui.data as SelectorData

  const select = (option: SelectorOption) => {
    if (answered) return
    setAnswered(true)
    onRespond(ui.id, "select", option.value, option.group ? `${option.group} › ${option.label}` : option.label)
  }

  // We only collapse at COLLAPSED_COUNT + 1 to avoid having a "see all" button
  // that only shows one extra item, ensuring that the "see all" button always reveal
  // at least 2 items
  const collapsible = options.length > COLLAPSED_COUNT + 1
  const visibleOptions = collapsible && !expanded ? options.slice(0, COLLAPSED_COUNT) : options

  // The search field is only offered once a collapsible list is expanded, where the
  // full list is long enough that scanning is tedious. Plain substring match is enough
  // for these small, in-memory lists.
  const searchable = collapsible && expanded
  const normalizedQuery = query.trim().toLowerCase()
  const renderedOptions =
    searchable && normalizedQuery
      ? visibleOptions.filter(option => option.label.toLowerCase().includes(normalizedQuery))
      : visibleOptions
  const noResults = searchable && normalizedQuery !== "" && renderedOptions.length === 0

  // Double-collection fields mark every option with its parent group. Group headers
  // only appear once expanded; the collapsed view is a flat combined list, matching
  // the mobile double-collection card. First-seen group order is preserved.
  const grouped = expanded && options.some(option => option.group)
  const groups = grouped
    ? [...new Set(renderedOptions.map(option => option.group))].map(group => ({
        group,
        options: renderedOptions.filter(option => option.group === group),
      }))
    : []

  const clearQuery = () => {
    setQuery("")
    searchInputRef.current?.focus()
  }

  const toggleExpanded = () => {
    setExpanded(value => {
      // Reset the filter when collapsing so re-expanding starts from the full list.
      if (value) setQuery("")
      return !value
    })
  }

  const renderIcon = (option: SelectorOption) => {
    const iconUrl = option.icon_url ?? cardIcon
    if (!iconUrl) return null
    const brandColor = option.brand_color ?? cardBrand
    return (
      <span className={styles["icon"]} style={brandColor ? { backgroundColor: `#${brandColor}` } : undefined}>
        <img src={iconUrl} alt={option.label} width="20" height="20" />
      </span>
    )
  }

  const renderOption = (option: SelectorOption) =>
    option.offline ? (
      <li key={option.value} className={styles["offline-row"]}>
        {renderIcon(option)}
        <span className={styles["text"]}>
          <span className={styles["label"]}>{option.label}</span>
          <span className={styles["offline-note"]}>Offline. Reconnection required.</span>
        </span>
        {option.reconnect_url ? (
          <a
            className={styles["reconnect"]}
            href={option.reconnect_url}
            target="_blank"
            rel="noreferrer"
            aria-label={`Reconnect ${option.label}`}
          >
            {parse(iconOpenNew)}
          </a>
        ) : null}
      </li>
    ) : (
      <li key={option.group ? `${option.group}:${option.value}` : option.value}>
        <button type="button" className={styles["option"]} disabled={answered} onClick={() => select(option)}>
          {renderIcon(option)}
          <span className={styles["label"]}>{option.label}</span>
        </button>
      </li>
    )

  const dismiss = () => onRespond(ui.id, "close", undefined)

  return (
    <CardShell prompt={prompt} onDismiss={dismiss}>
      {searchable ? (
        <div className={styles["search"]}>
          <span className={styles["search-icon"]} aria-hidden="true">
            {parse(iconSearch)}
          </span>
          <input
            ref={searchInputRef}
            type="text"
            className={styles["search-input"]}
            placeholder="Search"
            aria-label="Search options"
            value={query}
            disabled={answered}
            onChange={event => setQuery(event.currentTarget.value)}
          />
          {query ? (
            <button
              type="button"
              className={styles["search-clear"]}
              aria-label="Clear search"
              disabled={answered}
              onClick={clearQuery}
            >
              {parse(iconX)}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className={expanded ? styles["body-fixed"] : undefined} data-card-fill={expanded ? "" : undefined}>
        {grouped ? (
          groups.map(({ group, options: groupOptions }) => (
            <div key={group} className={styles["group"]}>
              <p className={styles["group-header"]}>{group}</p>
              <ul className={styles["options"]}>{groupOptions.map(renderOption)}</ul>
            </div>
          ))
        ) : (
          <ul className={styles["options"]}>{renderedOptions.map(renderOption)}</ul>
        )}
        {noResults ? (
          <div className={styles["no-results"]}>
            <p className={styles["no-results-title"]}>No results found</p>
            <p className={styles["no-results-subtitle"]}>Try a different search query.</p>
          </div>
        ) : null}
      </div>
      {/* On no results the toggle is hidden: collapsing there would only bury the
          empty state, and clearing the query is the way back to the full list. */}
      {collapsible && !noResults ? (
        <button type="button" className={styles["toggle"]} aria-expanded={expanded} onClick={toggleExpanded}>
          <span className={styles["toggle-icon"]} aria-hidden="true">
            {parse(expanded ? iconCollapse : iconExpand)}
          </span>
          <span className={styles["label"]}>{expanded ? "Minimize full list" : "Expand to full list"}</span>
        </button>
      ) : null}
      {somethingElse ? (
        <ul className={styles["options"]}>
          <li>
            <button type="button" className={styles["option"]} disabled={answered} onClick={dismiss}>
              <span className={styles["label"]}>Something else…</span>
            </button>
          </li>
        </ul>
      ) : null}
    </CardShell>
  )
}

export default SelectorCard
